import { useEffect, useMemo, useState } from 'react';
import { useListUrlState } from '@/components/lists';
import { CompactBand } from '@/components/CompactBand';
import './calendar.css';
import {
  MOCK_TODAY_ISO,
  MOCK_NOW_MIN,
  THIS_WEEK_MONDAY,
  NEXT_WEEK_MONDAY,
  PROVIDERS,
  addDaysIso,
  mondayOf,
  labelDayShort,
  labelWeekRange,
  visitsForDate,
  visitById,
  providerColumnsForDate,
  dayVisitCount,
  dayNotReadyCount,
  weekVisitCount,
  weekKnownNotReadyCount,
  weekdaysOf,
  nextVisitDateOnOrAfter,
  type Provider,
  type Visit,
} from './readinessMock';
import { DayGrid, DayGridSkeleton } from './components/DayGrid';
import { WeekAgenda, WeekSkeleton } from './components/WeekAgenda';
import { VisitModal } from './components/VisitModal';

type UrlKey = 'view' | 'date' | 'week' | 'visit' | 'filter' | 'provider';

function providerName(id: string): string {
  return PROVIDERS.find((p) => p.id === id)?.name ?? '';
}

/** Visits in time order (for ↑/↓ panel navigation and day-of counts). */
function orderedVisits(iso: string, providerId: string): Visit[] {
  return visitsForDate(iso)
    .filter((v) => providerId === 'all' || v.providerId === providerId)
    .slice()
    .sort((a, b) => a.startMin - b.startMin || a.providerId.localeCompare(b.providerId));
}

export function RcmCalendarView() {
  const [s, patch] = useListUrlState<UrlKey>({
    view: 'day',
    date: MOCK_TODAY_ISO,
    week: THIS_WEEK_MONDAY,
    visit: '',
    filter: 'all',
    provider: 'all',
  });

  const isWeek = s.view === 'week';
  const filterNotReady = s.filter === 'not-ready';
  const [explainerOpen, setExplainerOpen] = useState(false);

  // Brief grid skeleton on first mount + when the day/week changes (rcm-22 §5).
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    const t = window.setTimeout(() => {
      setLoading(false);
    }, 350);
    return () => {
      window.clearTimeout(t);
    };
  }, [s.view, s.date, s.week]);

  const selected = s.visit ? visitById(s.visit) : null;
  const openVisit = !isWeek && selected?.dateIso === s.date ? selected : null;

  // ---- keyboard: Esc closes, ↑/↓ move between visits in time order ----
  useEffect(() => {
    if (!openVisit) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        patch({ visit: '' });
        return;
      }
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const ordered = orderedVisits(s.date, s.provider);
        const idx = ordered.findIndex((v) => v.id === openVisit.id);
        if (idx < 0) return;
        const nextIdx = e.key === 'ArrowDown' ? idx + 1 : idx - 1;
        const next = ordered[nextIdx];
        if (next) patch({ visit: next.id });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [openVisit, s.date, s.provider, patch]);

  const allDayColumns = useMemo(() => providerColumnsForDate(s.date), [s.date]);
  const dayColumns = useMemo(
    () => (s.provider === 'all' ? allDayColumns : allDayColumns.filter((c) => c.provider.id === s.provider)),
    [allDayColumns, s.provider],
  );
  const weekDays = useMemo(() => weekdaysOf(s.week), [s.week]);
  const dayVisits = useMemo(
    () => visitsForDate(s.date).filter((v) => s.provider === 'all' || v.providerId === s.provider),
    [s.date, s.provider],
  );

  const providerOptions: Provider[] = useMemo(() => {
    if (isWeek) {
      const ids = new Set<string>();
      for (const iso of weekDays) for (const v of visitsForDate(iso)) ids.add(v.providerId);
      return PROVIDERS.filter((p) => ids.has(p.id));
    }
    return dayColumns.map((c) => c.provider);
  }, [isWeek, weekDays, dayColumns]);

  // ---- headline ----
  const headline = useMemo(() => {
    if (isWeek) {
      const n = weekVisitCount(s.week, s.provider);
      const nr = weekKnownNotReadyCount(s.week, s.provider);
      const prefix =
        s.week === THIS_WEEK_MONDAY
          ? 'This week'
          : s.week === NEXT_WEEK_MONDAY
            ? 'Next week'
            : `Week of ${labelDayShort(s.week)}`;
      return (
        <>
          {prefix}, {n} visits.{nr > 0 ? <em> {nr} aren&rsquo;t ready yet.</em> : null}
        </>
      );
    }
    const n = dayVisitCount(s.date, s.provider);
    const nr = dayNotReadyCount(s.date, s.provider);
    if (s.date === MOCK_TODAY_ISO) {
      return (
        <>
          Today, {n} visits.{nr > 0 ? <em> {nr} aren&rsquo;t ready.</em> : null}
        </>
      );
    }
    return (
      <>
        {labelDayShort(s.date)}: {n} visits.{nr > 0 ? <em> {nr} aren&rsquo;t ready.</em> : null}
      </>
    );
  }, [isWeek, s.week, s.date, s.provider]);

  const notReadyCount = isWeek
    ? weekKnownNotReadyCount(s.week, s.provider)
    : dayNotReadyCount(s.date, s.provider);

  // ---- band right: the explainer link ----
  const bandRight = (
    <button
      type="button"
      className="cbd-lk"
      style={{ border: 0, background: 'none', cursor: 'pointer', padding: 0 }}
      onClick={() => {
        setExplainerOpen(true);
      }}
    >
      Insurance checks run the day before each visit
    </button>
  );

  // ---- tabs row left: Day | Week, date / week stepper, Today / This week ----
  const tabsLeft = (
    <div className="rcal-tabs-left">
      <span className="rcal-seg" role="group" aria-label="Day or week">
        <button
          type="button"
          aria-pressed={!isWeek}
          className={!isWeek ? 'on' : ''}
          onClick={() => {
            patch({ view: 'day', visit: '' });
          }}
        >
          Day
        </button>
        <button
          type="button"
          aria-pressed={isWeek}
          className={isWeek ? 'on' : ''}
          onClick={() => {
            patch({ view: 'week', week: mondayOf(s.date), visit: '' });
          }}
        >
          Week
        </button>
      </span>
      {isWeek ? (
        <span className="rcal-dn">
          <button
            type="button"
            aria-label="Previous week"
            onClick={() => {
              patch({ week: addDaysIso(s.week, -7) });
            }}
          >
            ‹
          </button>
          {labelWeekRange(s.week)}
          <button
            type="button"
            aria-label="Next week"
            onClick={() => {
              patch({ week: addDaysIso(s.week, 7) });
            }}
          >
            ›
          </button>
        </span>
      ) : (
        <span className="rcal-dn">
          <button
            type="button"
            aria-label="Previous day"
            onClick={() => {
              patch({ date: addDaysIso(s.date, -1), visit: '' });
            }}
          >
            ‹
          </button>
          {labelDayShort(s.date)}
          <button
            type="button"
            aria-label="Next day"
            onClick={() => {
              patch({ date: addDaysIso(s.date, 1), visit: '' });
            }}
          >
            ›
          </button>
        </span>
      )}
      <button
        type="button"
        className="rcal-today"
        onClick={() => {
          if (isWeek) patch({ week: THIS_WEEK_MONDAY });
          else patch({ date: MOCK_TODAY_ISO, visit: '' });
        }}
      >
        {isWeek ? 'This week' : 'Today'}
      </button>
    </div>
  );

  // ---- tabs row right: All visits | Not ready, provider, (location) ----
  const tabsRight = (
    <>
      <span className="rcal-seg" role="group" aria-label="Readiness filter">
        <button
          type="button"
          aria-pressed={!filterNotReady}
          className={!filterNotReady ? 'on' : ''}
          onClick={() => {
            patch({ filter: 'all' });
          }}
        >
          All visits
        </button>
        <button
          type="button"
          aria-pressed={filterNotReady}
          className={filterNotReady ? 'on' : ''}
          onClick={() => {
            patch({ filter: 'not-ready' });
          }}
        >
          Not ready <span className="rcal-mono">· {notReadyCount}</span>
        </button>
      </span>
      <select
        className="rcal-csel"
        aria-label="Provider"
        value={s.provider}
        onChange={(e) => {
          patch({ provider: e.target.value, visit: '' });
        }}
      >
        <option value="all">All providers</option>
        {providerOptions.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
    </>
  );

  // ---- body ----
  return (
    <div className="rcal">
      <CompactBand headline={headline} right={bandRight} tabs={tabsLeft} tabsRight={tabsRight} />

      <div className="rcal-body">
        {isWeek ? (
          loading ? (
            <WeekSkeleton />
          ) : (
            <WeekAgenda
              days={weekDays}
              providerId={s.provider}
              filterNotReady={filterNotReady}
              onOpenVisit={(iso, visitId) => {
                patch({ view: 'day', date: iso, visit: visitId });
              }}
              onOpenDay={(iso) => {
                patch({ view: 'day', date: iso, visit: '' });
              }}
            />
          )
        ) : loading ? (
          <DayGridSkeleton columnCount={Math.max(1, dayColumns.length)} />
        ) : dayColumns.length === 0 ? (
          <EmptyDay dateIso={s.date} />
        ) : (
          <>
            <DayGrid
              columns={dayColumns}
              visits={dayVisits}
              isToday={s.date === MOCK_TODAY_ISO}
              nowMin={MOCK_NOW_MIN}
              selectedId={openVisit ? openVisit.id : null}
              filterNotReady={filterNotReady}
              onSelect={(id) => {
                patch({ visit: id });
              }}
            />
            {openVisit ? (
              <VisitModal
                key={openVisit.id}
                visit={openVisit}
                providerName={providerName(openVisit.providerId)}
                dayLabel={s.date === MOCK_TODAY_ISO ? 'Today' : labelDayShort(s.date)}
                onClose={() => {
                  patch({ visit: '' });
                }}
              />
            ) : null}
          </>
        )}
      </div>

      {explainerOpen ? (
        <div
          className="rcal-scrim"
          role="presentation"
          onClick={() => {
            setExplainerOpen(false);
          }}
        >
          <div
            className="rcal-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="How insurance checks work"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <h2>How insurance checks work</h2>
            <p>
              ELVA verifies each visit&rsquo;s insurance the day before, so the readiness you see is
              from last night&rsquo;s check. It confirms the plan is active, reads the remaining
              yearly max and deductible, and estimates what the patient will owe.
            </p>
            <p>
              When something is missing — a pre-auth with no answer, a plan that ended, no insurance
              on file — the visit shows the reason in words, and what happens next. A visit booked
              after last night&rsquo;s run is checked tonight.
            </p>
            <div className="rcal-dialog-close">
              <button
                type="button"
                className="rcal-btn primary"
                onClick={() => {
                  setExplainerOpen(false);
                }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function EmptyDay({ dateIso }: { dateIso: string }) {
  const next = nextVisitDateOnOrAfter(dateIso);
  return (
    <section className="rcal-card rcal-grid-card">
      <div className="rcal-empty">
        <p className="rcal-empty-title">No visits on {labelDayShort(dateIso)}.</p>
        {next ? (
          <p className="rcal-empty-meta">Next visits: {labelDayShort(next)}</p>
        ) : (
          <p className="rcal-empty-meta">Nothing else on the schedule.</p>
        )}
      </div>
    </section>
  );
}
